import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  HardDrive,
  History,
  KeyRound,
  LayoutDashboard,
  List,
  CircleDollarSign,
  Package,
  PackageCheck,
  Pencil,
  Shield,
  SlidersHorizontal,
  Truck,
  Users,
  Wallet,
} from "lucide-react";
import type { PermissionLevel, SectionKey } from "@/lib/modulePermissions";

export const ROLE_LEVELS: { id: PermissionLevel; label: string }[] = [
  { id: "none", label: "Ninguno" },
  { id: "read", label: "Lectura" },
  { id: "full", label: "Completo" },
];

export const ROLE_MODULE_GROUPS: {
  id: string;
  title: string;
  moduleIds: number[];
}[] = [
  { id: "op", title: "Operación", moduleIds: [16, 3, 12, 2, 7, 15, 14] },
  { id: "cat", title: "Catálogo y stock", moduleIds: [6, 8, 1, 4] },
  { id: "fin", title: "Finanzas", moduleIds: [10, 13, 5] },
  { id: "adm", title: "Administración", moduleIds: [9, 11] },
];

export const ROLE_MODULE_HINTS: Record<number, string> = {
  1: "ABM, pedidos, remitos y pagos se tildan adentro",
  2: "Estado de cuenta, comprobantes y ABM se tildan adentro",
  6: "Listado, ABM e importar se tildan; Configurador tiene nivel propio",
  7: "Pedidos de reposición (no órdenes de compra a proveedor)",
  9: "Los 8 informes comparten este flag (P0)",
  3: "Historial y columna Total se tildan adentro",
  10: "Movimientos y cierre diario se tildan adentro",
  11: "Tablas maestras; secciones sensibles abajo con nivel propio",
  16: "Cards del Inicio. Tildar una card pone Completo en el módulo del menú (Ventas, Remitos Vtas, Proveedores, Reportes). Destildar solo oculta la card.",
};

/** `toggle` = ver/ocultar pestaña. `level` = Ninguno / Lectura / Completo (P0).
 *  `opt_in` = flag default OFF (ausente ≠ hereda); ON = full explícito. */
export type NestedSectionKind = "toggle" | "level" | "opt_in";

export type NestedSectionDef = {
  id: SectionKey;
  name: string;
  hint: string;
  kind: NestedSectionKind;
  icon: LucideIcon;
  gradient: string;
};

export const ROLE_MATRIX_ONLY_MODULES: {
  id: number;
  name: string;
  icon: LucideIcon;
  gradient: string;
}[] = [
  {
    id: 16,
    name: "Dashboard / Inicio",
    icon: LayoutDashboard,
    gradient: "from-sky-500 to-indigo-500",
  },
];

export const MODULE_NESTED_SECTIONS: Record<number, NestedSectionDef[]> = {
  16: [
    {
      id: "remitos_vtas",
      name: "Remitos Vtas",
      hint: "Pendientes de entrega en el Inicio. Tildar pone Remitos Vtas en Completo.",
      kind: "toggle",
      icon: PackageCheck,
      gradient: "from-cyan-500 to-blue-500",
    },
    {
      id: "remitos_compras",
      name: "Remitos Compras",
      hint: "Pendientes de recepción en el Inicio. Tildar pone Proveedores en Completo.",
      kind: "toggle",
      icon: Truck,
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      id: "facturas",
      name: "Facturas a finalizar",
      hint: "Comprobantes pendientes en el Inicio. Tildar pone Ventas en Completo.",
      kind: "toggle",
      icon: FileText,
      gradient: "from-green-500 to-emerald-500",
    },
    {
      id: "estadisticas",
      name: "Estadísticas",
      hint: "Gráfico y serie mensual del Inicio. Tildar pone Reportes en Completo.",
      kind: "toggle",
      icon: BarChart3,
      gradient: "from-violet-500 to-purple-500",
    },
  ],
  1: [
    {
      id: "abm",
      name: "ABM",
      hint: "Alta y edición de proveedores.",
      kind: "toggle",
      icon: Pencil,
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      id: "pedidos",
      name: "Pedidos",
      hint: "Órdenes de compra a proveedor.",
      kind: "toggle",
      icon: ClipboardList,
      gradient: "from-sky-500 to-blue-600",
    },
    {
      id: "remitos",
      name: "Remitos",
      hint: "Remitos de compra.",
      kind: "toggle",
      icon: Truck,
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      id: "pagos",
      name: "Pagos",
      hint: "Pagos a proveedor.",
      kind: "toggle",
      icon: Banknote,
      gradient: "from-emerald-500 to-green-600",
    },
  ],
  2: [
    {
      id: "estado_cuenta",
      name: "Estado de cuenta",
      hint: "Saldos y movimientos del cliente.",
      kind: "toggle",
      icon: Users,
      gradient: "from-purple-500 to-pink-500",
    },
    {
      id: "comprobantes",
      name: "Comprobantes",
      hint: "Facturas y NC del cliente.",
      kind: "toggle",
      icon: FileText,
      gradient: "from-violet-500 to-purple-500",
    },
    {
      id: "abm",
      name: "ABM Clientes",
      hint: "Alta y edición de fichas.",
      kind: "toggle",
      icon: Pencil,
      gradient: "from-purple-500 to-pink-500",
    },
  ],
  3: [
    {
      id: "historial",
      name: "Historial",
      hint: "Facturación de meses anteriores, todas las sucursales. Destildar oculta la sección y el API responde 403.",
      kind: "toggle",
      icon: History,
      gradient: "from-green-500 to-emerald-500",
    },
    {
      id: "columna_total",
      name: "Columna Total",
      hint: "Montos en el listado de facturas. Destildar omite Total en la tabla y en el JSON de list. Ver y PDF siguen con importes.",
      kind: "toggle",
      icon: CircleDollarSign,
      gradient: "from-emerald-500 to-teal-500",
    },
    {
      id: "crear_proveedor",
      name: "Crear proveedor",
      hint: "Alta de proveedor solo desde el ítem personalizado (Otras fábricas). Default off. No abre el módulo Proveedores.",
      kind: "opt_in",
      icon: Truck,
      gradient: "from-blue-500 to-cyan-500",
    },
  ],
  6: [
    {
      id: "listado",
      name: "Listado",
      hint: "Consulta de existencias.",
      kind: "toggle",
      icon: List,
      gradient: "from-indigo-500 to-blue-500",
    },
    {
      id: "abm",
      name: "ABM Stock",
      hint: "Alta y edición de productos / existencias.",
      kind: "toggle",
      icon: Package,
      gradient: "from-indigo-500 to-blue-500",
    },
    {
      id: "importar",
      name: "Importar",
      hint: "Importación masiva Excel. Solo con Completo.",
      kind: "toggle",
      icon: FileSpreadsheet,
      gradient: "from-teal-500 to-cyan-500",
    },
    {
      id: "configurador",
      name: "Configurador de ítems",
      hint: "Árbol de campos personalizados. Independiente del ABM de stock.",
      kind: "level",
      icon: SlidersHorizontal,
      gradient: "from-indigo-500 to-blue-500",
    },
  ],
  10: [
    {
      id: "movimientos",
      name: "Movimientos",
      hint: "Movimientos de caja.",
      kind: "toggle",
      icon: Wallet,
      gradient: "from-emerald-500 to-green-500",
    },
    {
      id: "cierre",
      name: "Cierre diario",
      hint: "Armado y cierre del día.",
      kind: "toggle",
      icon: ClipboardCheck,
      gradient: "from-emerald-500 to-teal-500",
    },
  ],
  11: [
    {
      id: "backups",
      name: "Backups",
      hint: "Respaldos, restauración y retención.",
      kind: "level",
      icon: HardDrive,
      gradient: "from-yellow-500 to-orange-600",
    },
    {
      id: "identidad",
      name: "Usuarios / Roles",
      hint: "Quién edita la matriz de permisos.",
      kind: "level",
      icon: Shield,
      gradient: "from-purple-500 to-purple-600",
    },
    {
      id: "arca",
      name: "ARCA / AFIP",
      hint: "Certificados y entorno fiscal.",
      kind: "level",
      icon: KeyRound,
      gradient: "from-violet-500 to-purple-600",
    },
  ],
};

/** Alias P0: secciones con radios (nivel propio). */
export const P0_SECTIONS_UNDER: Record<number, NestedSectionDef[]> = Object.fromEntries(
  Object.entries(MODULE_NESTED_SECTIONS).map(([id, kids]) => [
    Number(id),
    kids.filter((k) => k.kind === "level"),
  ]),
) as Record<number, NestedSectionDef[]>;
